import React from 'react';
import { Box } from '@mui/material';
import ParticipantAvatar from './ParticipantAvatar';
import StatusMessage from './StatusMessage';
import MicOffIndicator from './MicOffIndicator';

/**
 * Main Video Display Component
 * Shows either remote video, local video, or avatar based on state
 */
function MainVideoDisplay({
  remoteVideoRef,
  localVideoRef,
  hasRemoteParticipant,
  isRemoteVideoEnabled,
  isRemoteScreenSharing,
  isVideoEnabled,
  isRemoteAudioEnabled,
  isAudioEnabled,
  remoteParticipantImage,
  remoteParticipantName,
  currentUserImage,
  currentUserName,
  statusMessage,
  statusMessageType,
}) {
  // Case 1: Remote participant screen sharing - ALWAYS show video element
  if (hasRemoteParticipant && isRemoteScreenSharing) {
    return (
      <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
        {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
        <video
          ref={remoteVideoRef}
          autoPlay
          playsInline
          muted={false}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            backgroundColor: '#000',
            display: 'block',
            visibility: 'visible',
            opacity: 1,
          }}
        />
        
        {!isRemoteAudioEnabled && (
          <Box sx={{ position: 'absolute', bottom: 16, right: 16 }}>
            <MicOffIndicator size="large" position="relative" />
          </Box>
        )}

        {statusMessage && (
          <StatusMessage 
            message={statusMessage} 
            type={statusMessageType} 
            position="top" 
          />
        )}
      </Box>
    );
  }

  // Case 1b: Remote participant with camera ON (but not screen sharing)
  if (hasRemoteParticipant && isRemoteVideoEnabled) {
    return (
      <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
        {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
        <video
          ref={remoteVideoRef}
          autoPlay
          playsInline
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            backgroundColor: '#000',
          }}
        />
        
        {!isRemoteAudioEnabled && (
          <Box sx={{ position: 'absolute', bottom: 16, right: 16 }}>
            <MicOffIndicator size="large" position="relative" />
          </Box>
        )}

        {statusMessage && (
          <StatusMessage 
            message={statusMessage} 
            type={statusMessageType} 
            position="top" 
          />
        )}
      </Box>
    );
  }

  // Case 2: Alone with camera ON
  if (!hasRemoteParticipant && isVideoEnabled) {
    return (
      <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
        {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
        <video
          ref={localVideoRef}
          autoPlay
          playsInline
          muted
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            transform: 'scaleX(-1)',
            backgroundColor: '#000',
          }}
        />
        
        {!isAudioEnabled && (
          <Box sx={{ position: 'absolute', bottom: 16, right: 16 }}>
            <MicOffIndicator size="large" position="relative" />
          </Box>
        )}

        {statusMessage && (
          <StatusMessage 
            message={statusMessage} 
            type={statusMessageType} 
            position="top" 
          />
        )}
      </Box>
    );
  }

  // Case 3: Camera OFF (either remote or local) - Show avatar
  return (
    <Box 
      sx={{ 
        position: 'relative', 
        width: '100%', 
        height: '100%', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center' 
      }}
    >
      <ParticipantAvatar
        image={hasRemoteParticipant ? remoteParticipantImage : currentUserImage}
        name={hasRemoteParticipant ? remoteParticipantName : currentUserName}
        isMuted={hasRemoteParticipant ? !isRemoteAudioEnabled : !isAudioEnabled}
        size="large"
        showName
        helperText="Camera is off"
      />

      {statusMessage && (
        <StatusMessage 
          message={statusMessage} 
          type={statusMessageType} 
          position="top" 
        />
      )}
    </Box>
  );
}

export default MainVideoDisplay;
